<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import { getThumbnailUrl, getUrlFromList } from '@/helpers/videoUrlUtils'
import VideoModalBase from './VideoModalBase.vue'

interface SampleVideo {
  url?: string
  name?: string
  date?: string
  duration?: number
  description?: string
  thumbnail_url?: string | null
  teacher_photo?: string
  teacher_name?: string
  teacher_user_id?: number
  teacher_bio_link?: string
}

const props = defineProps({
  modelValue: {
    type: Array as () => SampleVideo[] | null,
    default: null
  }
})

const emit = defineEmits<{
  'update:modelValue': [value: null]
}>()

const modal = useTemplateRef('modal')
const selectedVideoIndex = ref(0)

watch(
  () => props.modelValue,
  () => {
    selectedVideoIndex.value = 0
  }
)

const videoObject = computed(() => {
  if (!props.modelValue) return {}
  return props.modelValue?.[selectedVideoIndex.value] || {}
})

watch(
  videoObject,
  () => {
    const modalElement = modal.value
    if (!modalElement) return

    const container = modalElement.$el?.querySelector('.v-rich-modal__content')
    if (!container) return

    container.scrollTo({ top: 0, behavior: 'smooth' })
  },
  { deep: true }
)

function formatDuration(value: number) {
  if (!value) return '0:00'
  const mins = Math.floor(value / 60)
  const secs = value % 60

  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getThumbnail(item: SampleVideo) {
  if (item.thumbnail_url) return item.thumbnail_url
  return getThumbnailUrl(getUrlFromList(item.url ?? ''))
}
</script>

<template>
  <VideoModalBase
    ref="modal"
    class="public-sample-modal"
    title="МирГоворит для учителя"
    :model-value="!!modelValue"
    :video-src="videoObject.url"
    @update:model-value="() => emit('update:modelValue', null)"
  >
    <template #playlist>
      <ul class="public-sample-modal__playlist">
        <li
          v-for="(v, i) in modelValue"
          :key="i"
          class="public-sample-modal__playlist-item"
          :class="{ 'public-sample-modal__playlist-item_selected': i === selectedVideoIndex }"
          @click="() => (selectedVideoIndex = i)"
        >
          <div
            class="public-sample-modal__playlist-item-thumbnail"
            :style="{ backgroundImage: `url(${getThumbnail(v)})` }"
          >
            <!-- TODO: how do we get duration? -->
            <div v-if="false" class="public-sample-modal__thumbnail-duration">
              {{ formatDuration(v.duration ?? 0) }}
            </div>

            <div v-if="i === selectedVideoIndex" class="public-sample-modal__thumbnail-playing-icon">
              <v-icon>mdi-play</v-icon>
            </div>
          </div>

          <span class="public-sample-modal__playlist-item-title" v-text="v.name" />

          <img class="public-sample-modal__playlist-item-avatar" :src="v.teacher_photo" />

          <span class="public-sample-modal__playlist-item-author" v-text="v.teacher_name" />
        </li>
      </ul>
    </template>

    <template #description>
      <div v-if="videoObject" class="d-flex flex-column">
        <div class="flex-adaptive justify-space-between mb-half">
          <h2 class="text-title-bold" v-text="videoObject.name" />
        </div>

        <div class="public-sample-modal__date mb-2" v-text="videoObject.date" />

        <div class="flex-adaptive mb-1">
          <span class="d-flex align-center">
            <img class="public-sample-modal__avatar mr-2" :src="videoObject.teacher_photo" />

            {{ videoObject.teacher_name }}
          </span>

          <div class="flex-spacer" />

          <v-button mode="primary text" :href="videoObject.teacher_bio_link" target="_blank">
            Визитка учителя
          </v-button>
        </div>

        <p class="public-sample-modal__description mb-3" v-html="videoObject.description" />
      </div>
    </template>
  </VideoModalBase>
</template>

<style lang="scss">
$mobileMaxWidth: 960px;

.public-sample-modal {
  &__playlist {
    display: flex;
    flex-direction: column;

    & > * {
      margin-bottom: 4px;
    }

    @include desktop {
      height: fit-content;
      overflow-y: auto;
    }
  }

  &__playlist-item {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: 1fr auto;
    gap: 8px 16px;
    grid-template-areas:
      'thumbnail title title'
      'thumbnail avatar author';

    align-items: center;

    cursor: pointer;
    padding: 8px;

    transition: opacity 0.1s ease;
    &:not(&_selected):hover {
      opacity: 0.8;
    }

    &_selected {
      border-radius: 8px;
      background: $lightenGray;
      cursor: default;
    }
  }

  &__playlist-item-thumbnail {
    grid-area: thumbnail;
    width: 150px;
    height: 88px;

    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    border-radius: 8px;
    position: relative;
  }

  &__thumbnail-duration {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;

    padding: 0 8px;
    border-radius: 4px;
    background: white;

    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__thumbnail-playing-icon {
    padding: 0 4px;
    border-radius: 4px;
    background: $blue;
    color: white;

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__playlist-item-title {
    grid-area: title;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  &__playlist-item-avatar {
    grid-area: avatar;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    object-fit: cover;
  }

  &__playlist-item-author {
    grid-area: author;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  &__playlist-item-body {
    display: flex;
    flex-direction: column;
    text-align: center;

    & > * {
      margin-bottom: 4px;
    }
  }

  &__date {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $darkGray;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 999px;
    object-fit: cover;
  }

  &__description {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;

    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
}
</style>
